import Choices from 'choices.js';
import 'choices.js/public/assets/styles/choices.min.css';
import '../assets/stylesheets/style/_choices_overwride.scss';

document.addEventListener('DOMContentLoaded', function () {
  const searchInput = document.getElementById('js-search-form');
  if (!searchInput) return;

  const choices = initChoices(searchInput);

  // デバウンスを使用して検索リクエストを遅延させる
  const debouncedSearch = debounce((searchTerm) => {
    fetch(`/api/search?query=${encodeURIComponent(searchTerm)}`)
      .then((response) => response.json())
      .then((data) => {
        choices.clearChoices();

        const userItems = (data.users ?? []).map((user) => ({
          value: `/c/${user.path_name}`,
          label: user.name,
          customProperties: { image: user.image_url },
        }));

        const topicItems = (data.topics ?? []).map((topic) => ({
          value: `/topics/${encodeURIComponent(topic.slug)}`,
          label: topic.name,
          customProperties: { icon: '<i class="fas fa-compass"></i>' },
        }));

        const tagItems = (data.tags ?? []).map((tag) => ({
          value: `/search/tags/${encodeURIComponent(tag.slug)}`,
          label: tag.name,
          customProperties: { icon: '<i class="fas fa-tag"></i>' },
        }));

        const keywordItems = data.query
          ? [
              {
                value: `/search?query=${encodeURIComponent(data.query)}`,
                label: `「 ${data.query} 」で検索`,
                customProperties: { icon: '<i class="fas fa-search"></i>' },
              },
            ]
          : [];

        appendGroup(choices, 'ユーザー検索', userItems);
        appendGroup(choices, 'トピック検索', topicItems);
        appendGroup(choices, 'タグ検索', tagItems);
        appendGroup(choices, 'キーワード検索', keywordItems);
      })
      .catch((error) => console.error('検索エラー:', error));
  }, 300);

  searchInput.addEventListener('search', function (event) {
    const searchTerm = event.detail.value;
    if (searchTerm !== '') {
      debouncedSearch(searchTerm);
    }
  });

  // 選択時のリダイレクト処理
  searchInput.addEventListener('choice', function (event) {
    const selectedChoice = event.detail;
    const url = selectedChoice.value;
    if (url) {
      window.location.href = url;
    }
  });
});

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

// サジェストのカテゴリ見出しと候補アイテムをまとめてドロップダウンに追加する。
// items が空のときは見出しごとスキップする。
function appendGroup(choices, headerLabel, items) {
  if (!items || items.length === 0) return;

  choices.setChoices(
    [{ label: headerLabel, disabled: true }],
    'value',
    'label',
    false
  );
  choices.setChoices(items);
}

// Choices.js が allowHTML: true で label を innerHTML として描画するため、
// API レスポンス由来の文字列をテンプレートに挿す前にエスケープして XSS を防ぐ。
//
// 適用範囲: 外部由来（ユーザー入力 / DB 由来）の文字列のみ。
// 例えば下のテンプレ内 customProperties.icon (`<i class="fas fa-..."></i>`) は
// このファイル内で組み立てた hardcoded リテラルのため適用不要。今後 customProperties に
// 外部由来の値を入れる場合は、入れた箇所で escapeHtml を通すこと。
function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function initChoices(searchInput) {
  return new Choices(searchInput, {
    shouldSort: false,
    searchEnabled: true,
    placeholder: true,
    allowHTML: true,
    // 虫眼鏡はヘッダー側（_header_v5.scss）で重ねているのでここでは付けない
    placeholderValue: 'キーワード・タグ・クリエイターを検索',
    loadingText: '検索中...',
    searchPlaceholderValue: 'キーワード・タグ・クリエイターを検索',
    removeItemButton: true,
    noResultsText: '結果がありません',
    itemSelectText: '選択',
    noChoicesText: '選択肢がありません',
    callbackOnCreateTemplates: function (template) {
      return {
        item: ({ classNames }, data) => {
          return template(`
            <div class="${classNames.item} ${data.highlighted ? classNames.highlightedState : classNames.itemSelectable}" data-item data-id="${data.id}" data-value="${data.value}" role="option" ${data.active ? 'aria-selected="true"' : 'aria-selected="false"'} ${data.disabled ? 'aria-disabled="true"' : ''}>
              ${
                !data.value
                  ? ''
                  : data.customProperties && data.customProperties.image
                    ? `<img src="${data.customProperties.image}" alt="" style="width: 25px; height: 25px; margin-right: 10px;vertical-align: middle;">`
                    : `<span style="display: inline-block; width: 25px; height: 25px; margin-right: 10px; vertical-align: middle; line-height: 25px; text-align: center;">${data.customProperties && data.customProperties.icon ? data.customProperties.icon : ''}</span>`
              }
              ${escapeHtml(data.label)}
            </div>
          `);
        },
        choice: ({ classNames }, data) => {
          return template(`
            <div class="${classNames.item} ${classNames.itemChoice} ${data.disabled ? classNames.itemDisabled : classNames.itemSelectable} ${data.groupId ? classNames.itemChoiceGroup : ''}" data-select-text="${this.config.itemSelectText}" data-choice ${data.disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'} data-id="${data.id}" data-value="${data.value}" ${data.groupId ? 'role="group"' : 'role="option"'}>
              ${
                !data.value
                  ? ''
                  : data.customProperties && data.customProperties.image
                    ? `<img src="${data.customProperties.image}" alt="" style="width: 25px; height: 25px; margin-right: 10px; vertical-align: middle;">`
                    : `<span style="display: inline-block; width: 25px; height: 25px; margin-right: 10px; vertical-align: middle; line-height: 25px; text-align: center;">${data.customProperties && data.customProperties.icon ? data.customProperties.icon : ''}</span>`
              }
              ${escapeHtml(data.label)}
            </div>
          `);
        },
      };
    },
  });
}
